import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  HistoryGranularity,
  HistoryPeriodSummaryDTO,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import {
  deltaPct,
  filterByTeam,
  formatPct,
  periodShortLabel,
  sumTotals,
} from "./closerAvailabilityHistoryStats.ts";

type Props = {
  granularity: HistoryGranularity;
  periods: HistoryPeriodSummaryDTO[];
  team: string | null;
  selectedPeriodStart: string | null;
  onSelect: (periodStart: string) => void;
};

export function HistoryTrendChart({
  granularity,
  periods,
  team,
  selectedPeriodStart,
  onSelect,
}: Props) {
  const data = periods.map((p) => {
    const totals = sumTotals(filterByTeam(p.closers, team));
    const hasClosure = p.closureTakenAt !== null;
    return {
      periodStart: p.periodStart,
      label: periodShortLabel(granularity, p.periodStart),
      opening: p.openingTakenAt ? totals.opening : null,
      closure: hasClosure ? totals.closure : null,
      deltaPct:
        hasClosure && p.openingTakenAt
          ? deltaPct(totals.closure, totals.opening)
          : null,
    };
  });

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart
        data={data}
        margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
        onClick={(e) => {
          const periodStart = e?.activePayload?.[0]?.payload?.periodStart;
          if (periodStart) onSelect(periodStart);
        }}
        className="cursor-pointer"
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis yAxisId="slots" tick={{ fontSize: 12 }} />
        <YAxis
          yAxisId="pct"
          orientation="right"
          tick={{ fontSize: 12 }}
          tickFormatter={(v: number) => `${v}%`}
        />
        <Tooltip
          formatter={(value, name) => {
            if (value === null || value === undefined) return ["—", name];
            if (name === "Δ %") return [formatPct(Number(value)), name];
            return [value, name];
          }}
        />
        <Bar
          yAxisId="slots"
          dataKey="opening"
          name="Capacity apertura"
          fill="#C7D5F5"
          radius={[3, 3, 0, 0]}
        >
          {data.map((d) => (
            <Cell
              key={d.periodStart}
              fill={
                d.periodStart === selectedPeriodStart ? "#93AEEA" : "#C7D5F5"
              }
            />
          ))}
        </Bar>
        <Bar
          yAxisId="slots"
          dataKey="closure"
          name="Slot effettivi chiusura"
          fill="#1E429F"
          radius={[3, 3, 0, 0]}
        >
          {data.map((d) => (
            <Cell
              key={d.periodStart}
              fill={
                d.periodStart === selectedPeriodStart ? "#0F2A6E" : "#1E429F"
              }
            />
          ))}
        </Bar>
        <Line
          yAxisId="pct"
          dataKey="deltaPct"
          name="Δ %"
          stroke="#DC2626"
          strokeWidth={2}
          dot={{ r: 3 }}
          connectNulls={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
